import { forwardRef } from "react";
import {
  CheckboxProps as CoreCheckboxProps,
  Checkbox as CoreCheckbox,
} from "./internal/Checkbox";
import { CheckboxGroup } from "./CheckboxGroup";
import { useTrackingId } from "../../hooks";
import { DataTrackingId } from "../../types";
import { childrenToString } from "../../internal/functions";

/**
 * Props for the Checkbox component
 * @property {boolean} [checked] - Controlled state
 * @property {boolean} [defaultChecked] - Uncontrolled state
 * @property {boolean} [indeterminate] - Indeterminate state of the checkbox
 * @property {boolean} [error] - Error state for the checkbox
 * @property {string | ReactElement} [label] - Label for checkbox
 * @property {string} [description] - Helper text description
 * @property {Object} [icons] - Icon overrides for different states
 * @property {(e?: ChangeEvent<HTMLInputElement>, state?: CheckboxState) => void} [onChange] - Change callback
 * @property {(e?: MouseEvent<HTMLInputElement>, state?: CheckboxState) => void} [onClick] - Click callback
 * @extends Omit<CoreCheckboxProps, "hideCheckbox">
 * @extends DataTrackingId
 */
export type CheckboxProps = Omit<CoreCheckboxProps, "hideCheckbox"> &
  DataTrackingId;

/**
 * Checkbox component for selecting one or more options from a list.
 *
 * Features:
 * - Controlled and uncontrolled state management
 * - Indeterminate state for partial selections
 * - Customizable icons for different states
 * - Error state with accessibility support
 * - Optional helper text and descriptions
 * - Full accessibility support with ARIA attributes
 * - Keyboard navigation support
 * - Layout utility props for positioning and spacing
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Checkbox
 *   label="Accept terms and conditions"
 *   onChange={(e, state) => console.log('Checked:', state.checked)}
 *   required
 * />
 */
export const Checkbox = Object.assign(
  forwardRef<HTMLInputElement, CheckboxProps>(
    function CheckboxInner(props, ref) {
      const data = {
        label: childrenToString(props.label),
        ariaLabel: props["aria-label"],
        ariaLabelledBy: props["aria-labelledby"],
        value: props.value,
      };

      const trackingId = useTrackingId({
        name: "Checkbox",
        data,
        hasOverride: !!props["data-tracking-id"],
      });
      return (
        <CoreCheckbox ref={ref} data-tracking-id={trackingId} {...props} />
      );
    },
  ),
  {
    /**
     * CheckboxGroup component for grouping related checkboxes together.
     *
     * Features:
     * - Groups multiple checkboxes with a common legend
     * - Supports required field indication
     * - Optional helper text and descriptions
     * - Full accessibility support with fieldset/legend
     * - Focus management for keyboard navigation
     * - Layout utility props for positioning and spacing
     * - Flexible layout direction (row/column)
     * - Screen reader support with hidden labels
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <CheckboxGroup legend="Select your interests" required>
     *   <Checkbox label="Technology" />
     *   <Checkbox label="Sports" />
     *   <Checkbox label="Music" />
     * </CheckboxGroup>
     */
    Group: CheckboxGroup,
  },
);
Checkbox.displayName = CoreCheckbox.displayName;
